// External
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';

// Internal
import { DevicesService } from '../../process/devices/devices.service';
import { MessageService } from '../../core/message.service';
import { OperatingSystems, ValuesService } from '../../../../common/values/values.service';
import { SubscriptionsService } from '../../process/subscriptions/subscriptions.service';
import { DevicesValuesService } from '../../../../common/values/devices.values';
import { UsefulService } from '../../global/useful/useful.service';
import { ProfilesService } from '../../process/profiles/profiles.service';
import { IThreatsChartService } from '../ithreats-chart/ithreats-chart.service';
import { DevicesDetailsService } from '../../process/devices/devices-details/devices-details.service';
import { SubscriptionsValuesService } from '../../../../common/values/subscriptions.values.service';
import { UtilsCommonService } from '../../../../common/utils/utils-common.service';
import { ConfigService } from '../../../../common/config/config.service';
import { AppsConfigService } from '../../../../common/config/apps.config.service';
import { AdobeDataLayerService } from '../../core/adobe.datalayer.service';
import { VulnerabilityType } from '../../../../common/models/devices/vulnerability.model';
@Injectable({
    providedIn: 'root'
})

export class IDevicesDetailsService {

    issuesTimers = {};

    constructor(
        readonly devicesService: DevicesService,
        readonly devicesValuesService: DevicesValuesService,
        readonly usefulService: UsefulService,
        readonly messageService: MessageService,
        readonly subscriptionsService: SubscriptionsService,
        readonly valuesService: ValuesService,
        readonly deviceDetectorService: DeviceDetectorService,
        readonly devicesDetailsService: DevicesDetailsService,
        readonly profilesService: ProfilesService,
        readonly iThreatsChartService: IThreatsChartService,
        readonly cookiesService: CookieService,
        readonly translateService: TranslateService,
        readonly subscriptionsValuesService: SubscriptionsValuesService,
        readonly utilsCommonService: UtilsCommonService,
        private readonly configService: ConfigService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly appsConfigService: AppsConfigService
    ) {}

    /**
     * Function that creates interface for device issues
     * @param deviceCrc  Device crc
     */
    createIssuesInterface(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const issues = this.usefulService.getNested(device, [], 'processed', "issues", 'list');
        let interfaceInfoCompleted = false;

        let issuesInterface = {
            list: [],
            hasIssues: false
        } as any;

        // daca nu am issues din connect, pot avea issues din cod
        if (!issues.length) {
            let subscriptionStatus = device.processed.subscriptionStatus;
            let pseudoIssueInterface = this.devicesValuesService.pseudoIssues[subscriptionStatus];
            if (pseudoIssueInterface) {
                issuesInterface = JSON.parse(JSON.stringify(pseudoIssueInterface));
                issuesInterface.showFlag = pseudoIssueInterface.show();
                if (!this.appsConfigService.showDeployFlow(this.valuesService.compatibleOsInfo[device.device_os]?.appId)) {
                    issuesInterface.action = "";
                    issuesInterface.actionName = "";
                    issuesInterface.showFlag = true;
                }
                interfaceInfoCompleted = true;
            }
        } else {
            // adaug issues din connect
            for (const issue of issues) {
                let issueDetails = this.devicesValuesService.issues[issue.app_id][issue.issue_id];
                if (issueDetails) {
                    issueDetails = JSON.parse(JSON.stringify({ ...issue, ...issueDetails }));
                    issueDetails.fixable = issue.fixable === 1 ? this.devicesValuesService.issueType['auto'] : this.devicesValuesService.issueType['manual']; // 1 auto / 0 manual
                    issuesInterface.list.push(issueDetails);
                    issuesInterface.hasIssues = true;
                }
            }
        }

        if (!device.interface) {
            device.interface = {};
        }

        device.interface.issues = issuesInterface;

        // daca am adaugat issues din cod, nu mai am nevoie de informatii suplimentare pt ca sunt deja adaugate
        if (!interfaceInfoCompleted) {
            this.addDetailsToIssues(deviceCrc);
        }
        // verific daca am issues in pending si adaug informatii in inetrfata de issues
        this.checkPendingIssues(deviceCrc);
    }

    /**
     * Function that check if current issues are still pending and adds info to issues interface
     * @param deviceCrc Device crc
     * @returns
     */
    checkPendingIssues(deviceCrc) {
        const localStorageKey = deviceCrc.toString().concat("_issues");
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const newIssues = this.usefulService.getNested(device, [], 'interface', "issues", 'list');
        const pendingObject: any =  JSON.parse(localStorage.getItem(localStorageKey));
        const fixTime: any =  parseInt(pendingObject?.timestamp);
        const oldIssues = pendingObject?.issues ? pendingObject?.issues : [];

        // daca nu avem obiect in local storage nu facem nimic
        if (!fixTime) {
            return;
        }

        // daca nu mai avem issues sau noile issues sunt diferite de cele din local storage, stergem ce avem in local storage
        // nu o sa mai avem pending issues
        if (!newIssues.length || this.differentSetOfIssues(newIssues, oldIssues)) {
            localStorage.removeItem(localStorageKey);
            return;
        }

        // daca conform timestampului din local storage cele 5 minute de pending au expirat, stergem issues din local storage
        // nu o sa mai avem pending issues
        const timeElapsed = new Date().getTime() - fixTime;
        const remainingPendingTime = this.devicesValuesService.pendingTimeInterval - timeElapsed;
        if (remainingPendingTime <= 0) {
            localStorage.removeItem(localStorageKey);
            return;
        }

        // daca inca nu au trecut 5 min, punem pe pending cu timpul ramas
        this.setPendingStatus(deviceCrc, remainingPendingTime);
    }

    /**
     * Function that compares new issues with old issues based on app_id, issue_id, created timestamp
     * An issue is different from another issue if all these attributes are different
     * @param newIssues
     * @param oldIssues
     * @returns Boolean
     */
    differentSetOfIssues(newIssues, oldIssues) {
        if (newIssues.length !== oldIssues.length) {
            return true;
        }

        for (const newIssue of newIssues) {
            let found = false;
            for (const oldIssue of oldIssues) {
                if (newIssue.issue_id === oldIssue.issue_id && newIssue.created === oldIssue.created && newIssue.app_id === oldIssue.app_id) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                return true;
            }
        }
        return false;
    }

    /**
     * Adds title, action and action name to issues interface
     * @param deviceCrc Device crc
     */
    addDetailsToIssues(deviceCrc) {
        const device          = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const issuesInterface = this.usefulService.getNested(device, {}, 'interface', "issues");
        const listOfIssues    = this.usefulService.getNested(issuesInterface, [], 'list');
        const someAutoIsssues = listOfIssues.some(item => item.fixable === this.devicesValuesService.issueType['auto']);

        // daca doar issues care se rezolva manual, pe buton nu o sa mai scrie Fix issues o sa scrie View issues
        // daca ai si niste issues automate, scrie Fix isses
        if (someAutoIsssues) {
            issuesInterface.actionName = listOfIssues.length === 1 ? 'devices.details.issues.action.fix.one.issue' : 'devices.details.issues.action.fix.more.issue';
            issuesInterface.action     = this.devicesValuesService.issuesAction.fix;
            issuesInterface.showFlag   = !this.restrictOwnerRightsForFamilyMembersDevices(device);
        } else {
            issuesInterface.actionName = listOfIssues.length === 1 ? 'devices.details.issues.action.view.one.issue' : 'devices.details.issues.action.view.more.issues';
            issuesInterface.action     = this.devicesValuesService.issuesAction.view;
        }
        issuesInterface.title      = listOfIssues.length === 1 ? 'devices.details.issues.one.issue.title' : 'devices.details.issues.more.issues.title';
    }

     /**
     * Adds title, action and action name to issues interface when issues are pending
     * @param deviceCrc Device crc
     */
    addDetailsToPendingIssues(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const issueDetails = this.usefulService.getNested(device, {}, 'interface', "issues");
        const listOfIssues = this.usefulService.getNested(device, {}, 'interface', "issues", "list");

        // ai pendig message diferit pt cazul cand ai doar issues automate fata de restul cazurilor
        for (const issue of issueDetails.list) {
            issue.pendingTitle = (issue.fixable === this.devicesValuesService.issueType['auto'])
                                                    ? 'devices.details.issues.pending.title.fix'
                                                    : 'devices.details.issues.pending.title.manual';
        }
        const noManualIssues = !(issueDetails.list.some(item => item.fixable === this.devicesValuesService.issueType['manual']));
        // tot asa, daca ai doar issues automate, titlul butonului trebuie sa se schimbe din Fix issus in View issues
        // asta pt ca deja ai apasat pe fix si requestul de fix s-a strimis deja, poti doar sa le vizualizezi progresul
        // tot conform logicii de mai sus, actiunea de pe buton este doar de view issues
        if (noManualIssues) {
            issueDetails.title = 'devices.details.issues.pending.title';
            if (issueDetails.list.length && issueDetails.list[0].issue_id !== '') {
                issueDetails.action = this.devicesValuesService.issuesAction.view;
                issueDetails.actionName = listOfIssues.length === 1 ? 'devices.details.issues.action.view.one.issue' : 'devices.details.issues.action.view.more.issues';
            }
        }
    }

    /**
     * Writes current issues along with the timestamp when user pressed fix issue, changes issues interface because now issues are pending
     * and sets a timeout to delete local sotrage info after time elapsed
     * If pending time is not equal to max pending time possible. that means that the funciton is called just to modify the interface and set the timer,
     * because the local storage entry is already there
     * @param deviceCrc Device crc
     * @param pendingTime Pending time that is left
     */
    setPendingStatus(deviceCrc, pendingTime) {
        if (this.issuesTimers.hasOwnProperty(deviceCrc)) {
            clearTimeout(this.issuesTimers[deviceCrc]);
        }

        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const issues = this.usefulService.getNested(device, [], 'interface', "issues", "list");

        this.addDetailsToPendingIssues(deviceCrc);
        if (pendingTime === this.devicesValuesService.pendingTimeInterval) {
            localStorage.setItem(deviceCrc.toString().concat("_issues"), JSON.stringify({ timestamp: new Date().getTime().toString(), issues: issues}));
        }
        const timer = setTimeout(() => { this.deletePendingIssues(deviceCrc); }, pendingTime);
        this.issuesTimers[deviceCrc] = timer;
    }

    /**
     * Deletes pending issues from storage and sends event for fix issues modal to change it's content
     * In fix issues modal user can see fixed/pendind/not fixed issues
     * @param deviceCrc Device crc
     */
    deletePendingIssues(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const localStorageKey = deviceCrc.toString().concat("_issues");
        if (localStorage.getItem(localStorageKey)) {
            localStorage.removeItem(localStorageKey);
            if (device) {
                this.devicesDetailsService.updateIssues(device.device_id);
                this.messageService.sendMessage(this.devicesValuesService.events.reloadListIssues, {});
            }
        }
    }

    getIssuesInterface(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        return this.usefulService.getNested(device, {}, 'interface', 'issues');
    }

    getIpsList(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const ipList = this.usefulService.getNested(device, {}, "ip_list");
        const ipv4 = [];
        const ipv6 = [];
        for (const ip in ipList) {
            if (ipList[ip].ipv4 && ipList[ip].ipv4.length) {
                for (const item of ipList[ip].ipv4) {
                    ipv4.push(item);
                }
            }

            if (ipList[ip].ipv6 && ipList[ip].ipv6.length) {
                for (const item of ipList[ip].ipv6) {
                    ipv6.push(item);
                }
            }
        }

        return ipv4.concat(ipv6);
    }

    createDeviceDetailsInterface(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        let details = this.usefulService.getNested(device, null, 'interface', 'details');

        if (!device.traffic) {
            device.traffic = {
                blocked: this.devicesValuesService.internet.UNPAUSED
            }
        }

        if (details) {
            return;
        }

        details        = this.devicesValuesService.statusInfo[device.processed.protectionStatus];
        details.paused = device.traffic && device.traffic.blocked === this.devicesValuesService.internet.PAUSED;
        details.ips    = this.getIpsList(deviceCrc);

        if (!device.interface) {
            device.interface = {};
        }

        const deviceInterface = device.interface;
        device.interface = {...deviceInterface,...details};
    }

    checkBoxManaged(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        if (this.devicesService.checkMethods.isBoxManaged(device)) {
            const devices = this.devicesService.getDevicesList();
            const box = devices.allObject[device.box_device_id];

            return (device.processed.isConnected && box.processed.protectionStatus === this.valuesService.protectionStatus.PROTECTED)
        }
        return false;
    }

    /**
     * Function that restricts owner rights for family members devices
     * @param device
     * @returns boolean
     */
    public restrictOwnerRightsForFamilyMembersDevices(device): boolean {
        return this.profilesService.currentGroupIsFamily()
            && device.user_id !== this.profilesService.getCurrentContextId();
    }

    createQuickActionsInterface(deviceId) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        if (!device) {
            return;
        }

        const deviceCrc = device.processed.device_crc;
        let hasQuickActions = false;
        let quickActionsCounter = 0;
        let vulnerabilityType = "";
        const deviceOs = this.devicesService.getDeviceOsBasedOnProtectionApp(device);
        const protectionAppId = device.processed.protectionApp;
        const protectionAppLevel = this.devicesService.getProtectionAppLevel(this.devicesService.retrieveDeviceById(deviceId));

        const quickActions = {
            [this.devicesValuesService.quickActions.malwareScan]: false,
            [this.devicesValuesService.quickActions.antitheft]: false,
            [this.devicesValuesService.quickActions.optimizer]: false,
            [this.devicesValuesService.quickActions.vulnerabilityScan]: false,
            [this.devicesValuesService.quickActions.scan]: false
        };

        if (protectionAppId) {
            for (const quickAction in quickActions) {
                if (!this.configService.config.quickActions[quickAction]
                    || this.restrictOwnerRightsForFamilyMembersDevices(device)) {
                    continue;
                }
                let quickActionFlagOs = false;
                let quickActionFlagApps = false;

                if (!quickActions[quickAction]) {
                    const quickActionsOs = this.devicesValuesService.quickActionsOS[quickAction];
                    quickActionFlagOs = quickActionsOs.has(deviceOs);
                    if (!quickActionFlagOs) {
                        continue;
                    }
                    const quickActionsApp = this.devicesValuesService.quickActionsApps[quickAction];
                    const quickActionsAppsIgnoredLevels = this.devicesValuesService.quickActionsAppsIgnoredLevels[quickAction];

                    const quickActionLevels = quickActionsApp[protectionAppId];
                    if (quickActionLevels === null) {
                        if (quickActionsAppsIgnoredLevels
                            && quickActionsAppsIgnoredLevels[protectionAppId]
                            && quickActionsAppsIgnoredLevels[protectionAppId].has(protectionAppLevel)) {
                            continue;
                        }
                        quickActionFlagApps = quickAction === this.devicesValuesService.quickActions.scan ? this.checkProtectionVersion(device) : true;
                    }
                    if (quickActionLevels) {
                        quickActionFlagApps = quickActionLevels.has(protectionAppLevel);
                    }
                }

                quickActions[quickAction] = quickActions[quickAction] || (quickActionFlagOs && quickActionFlagApps);
                hasQuickActions = hasQuickActions || quickActions[quickAction];
                if (quickActions[quickAction]) {
                    quickActionsCounter++;
                }
            }
        }

        if (quickActions[this.devicesValuesService.quickActions.vulnerabilityScan]) {
            vulnerabilityType = this.devicesValuesService.tasksName.vulnerability.scan;
        }

        quickActions[this.devicesValuesService.quickActions.vulnerabilityScan] = quickActions[this.devicesValuesService.quickActions.vulnerabilityScan]
                                                                                || this.checkBoxManaged(deviceCrc);
        hasQuickActions = hasQuickActions || quickActions[this.devicesValuesService.quickActions.vulnerabilityScan];

        if (quickActions[this.devicesValuesService.quickActions.vulnerabilityScan] && !vulnerabilityType) {
            vulnerabilityType = this.devicesValuesService.tasksName.vulnerability.assessment;
        }

        if (!device.interface) {
            device.interface = {};
        }

        device.interface.quickActions = quickActions;
        device.interface.hasQuickActions = hasQuickActions;
        device.interface.hasOnlyOneQuickAction = quickActionsCounter === 1;
        device.interface.vulnerabilityType = vulnerabilityType;
    }

    checkProtectionVersion(device) {
        const deviceOs = device.device_os;
        const protectionAppId = device.processed.protectionApp;

        if (deviceOs !== OperatingSystems.IOS) {
            return true;
        }

        const minimumVersion        = 6;
        const minimumMinorVersion   = 2;

        const appObj = device.processed.apps_obj[protectionAppId];
        const installedVersion      = parseInt(appObj.installed_version.split('.')[0], 10);
        const installedMinorVersion = parseInt(appObj.installed_version.split('.')[1], 10);

        return installedVersion > minimumVersion || (installedVersion === minimumVersion && installedMinorVersion >= minimumMinorVersion);
    }

    createPortForwardInterface(deviceId) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        const ports = device?.processed?.portForward?.port_fwd ?? [];
        for (const port of ports) {
            if (!port.interface) {
                port.interface = {};
            }
            port.interface.attemptedRemove = false;
        }
    }

    createThreatsChartInterface(deviceCrc) {
        let device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        let threats        = this.usefulService.getNested(device, {}, 'processed', 'threats');
        let reports        = this.usefulService.getNested(threats, [], 'countThreats', 'all');
        let devicesIds     = [ { 'device_id': device.device_id } ];
        this.iThreatsChartService.createThreatsChartInterface(threats, reports, devicesIds);
    }

    getThreatsInterface(deviceCrc, errorState) {
        let device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        let threatsData = {};
        threatsData["threatsDetails"] = this.usefulService.getNested(device, null, 'processed', 'threats', 'interface');

        if (!threatsData["threatsDetails"]) {
            return null;
        }

        threatsData["errorStateThreats"] = errorState;
        threatsData["devicesInterface"] = {empty : false}
        return threatsData;
    }

    hasIPV4Setting(deviceCrc) {
        let device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        let ipList = device.ip_list ? device.ip_list : {};

        for (const mac in ipList) {

            let ipv4 = ipList[mac].ipv4;
            if (Array.isArray(ipv4) && ipv4.length > 0) {
                return true;
            }
        }
        return false;
    }

    createSettingsInterface(deviceCrc) {
        let device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        let settings = {
            [this.devicesValuesService.settings.remove]: false,
            [this.devicesValuesService.settings.rename]: false,
            [this.devicesValuesService.settings.update]: false,
            [this.devicesValuesService.settings.profile]: false,
            [this.devicesValuesService.settings.ip]: false,
            [this.devicesValuesService.settings.portForwarding]: false
        };

        if (!this.devicesService.checkMethods.isBox(device)) {
            for (const setting in settings) {
                if (this.devicesValuesService.restrictedFamilyMembersDeviceSettings.has(setting)
                    && this.restrictOwnerRightsForFamilyMembersDevices(device)) {
                    continue;
                }

                let settingsApps = this.devicesValuesService.settingsApps[setting];
                if (settingsApps) {

                    // for everything else (other setting modals)
                    let apps = device.apps ? device.apps : [];
                    for (const app of apps) {
                        let appId = app.app_id;
                        let state = app.state;
                        if (state && settingsApps.has(appId)) {
                            settings[setting] = true;
                        }
                    }

                    // for ip forward & port forward
                    if (this.devicesService.checkMethods.isBoxManaged(device)) {
                        let boxAppId = device.box_app_id ? device.box_app_id : "";
                        if (settingsApps.has(boxAppId) && this.hasIPV4Setting(deviceCrc)) {
                            settings[setting] = true;
                        }
                    }
                } else {
                    settings[setting] = true;
                }
            }
        }

        if (!device.interface) {
            device.interface = {};
        }

        device.interface.settings = settings;
    }

    createRenameInterface(deviceId) {
        let device = this.devicesService.retrieveDeviceById(deviceId);
        const protectionAppId = device.processed.protectionApp;

        let rename = {
            showAppPin: false,
            appPin: null,
            isBox: this.devicesService.checkMethods.isBox(device)
        };

        if (protectionAppId === this.valuesService.bundleBMS) {
            let appPin = this.usefulService.getNested(device, null, 'processed', 'device_settings', this.valuesService.bundleBMS, this.valuesService.deviceSettings.pin);
            if (appPin !== null && device.device_os === OperatingSystems.ANDROID) {
                rename.appPin = appPin;
                rename.showAppPin = true;
            }
        }

        if (!device.interface) {
            device.interface = {};
        }

        device.interface.rename = rename;
    }

    createAntitheftLocationInterface(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const antitheft = this.usefulService.getNested(device, {}, 'processed', "antitheft");
        const location = this.usefulService.getNested(antitheft, {}, 'location', 'info');

        if (!antitheft.location.interface) {
            antitheft.location.interface = {};
        }

        if (location.lat !== this.devicesValuesService.notCoords && location.long !== this.devicesValuesService.notCoords) {

            const ipTime = this.convertTimestampToDMYHMS(location.timestamp);
            const ipTimestamp = location.timestamp * 1000;
            const locatedTime = this.usefulService.timeAgo(location.timestamp * 1000);

            antitheft.location.interface.lastUpdate = locatedTime;
            antitheft.location.interface.ipTimestamp = ipTimestamp;
            antitheft.location.interface.ipTime = ipTime;
        }
    }

    //todo de facut o metoda mai draguta
    convertTimestampToDMYHMS(timestamp) {
        // day, month year hh:mm:ss
        const date = new Date(timestamp * 1000);
        return `${this.translateService.instant(this.valuesService.daysWeek[date.getDay()])},
                ${date.getUTCDate()}
                ${this.translateService.instant('platform.screentime.month.' + (date.getMonth()+1)).slice(0,3)}
                ${date.getFullYear()}
                ${(date.getHours() < 10 ? '0' : '') + date.getHours()}:
                ${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}:
                ${(date.getSeconds() < 10 ? '0' : '') + date.getSeconds()}`;
    }

    createAntitheftSnapInterface(deviceCrc) {
        const device    = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const antitheft = this.usefulService.getNested(device, {}, 'processed', "antitheft");
        const snapPhoto = this.usefulService.getNested(antitheft, [], 'snapPhoto');

        for (const photo of snapPhoto) {
            photo.interface = {};
            photo.interface.date = this.convertTimestampToDMYHMS(photo.timestamp);
            photo.interface.dateTimestamp = photo.timestamp*1000;
        }
    }

    createOneClickOptimizerInterfaceOptimize(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const optimize = this.usefulService.getNested(device, {}, 'processed', 'optimizer', 'optimize');

        if (!optimize.interface) {
            optimize.interface = {};
        }

        let details = {};
        let noReportAvailable = false;
        let newRaport = false;

        if (localStorage.getItem(deviceCrc + this.devicesValuesService.flagReportRead) === 'false') {
            // true inseamna citit, false inseamna necitit
            newRaport = true;
        }

        switch(optimize.task_state) {
            case this.devicesValuesService.commandsState.pending:
                details = this.devicesValuesService.deviceOptimizeCases.pending;
                break;
            case this.devicesValuesService.commandsState.running:
                details = this.devicesValuesService.deviceOptimizeCases.running;
                break;
            case this.devicesValuesService.commandsState.idle:
                if (!optimize.summary) {
                    details = this.devicesValuesService.deviceOptimizeCases.noReportAvailable;
                    noReportAvailable = true;
                } else {
                    details = this.devicesValuesService.deviceOptimizeCases.hasRaport;
                }
                break;
            default:
                details = this.devicesValuesService.deviceOptimizeCases.noReportAvailable;
                noReportAvailable = true;
                break;
        }

        optimize.interface.status = details;
        optimize.interface.status.newBadge = newRaport;

        if (!noReportAvailable) {
            this.createLatestRaportInfo(deviceCrc);
        }
    }

    createLatestRaportInfo(deviceCrc) {
        const device   = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const optimize = this.usefulService.getNested(device, {}, 'processed', 'optimizer', 'optimize');

        if (!optimize.interface) {
            optimize.interface = {};
        }

        if (optimize.last_summary_timestamp) {
            optimize.interface.dateTime = this.usefulService.timeAgo(optimize.last_summary_timestamp);
            optimize.interface.date = this.usefulService.convertDate(optimize.last_summary_timestamp);
        }

        if (optimize.summary && !this.utilsCommonService.isEmptyObject(optimize.summary)) {
            optimize.interface.report = {};
            optimize.interface.report.diskSpace    = {resolved: {}, report: []};
            optimize.interface.report.registryKeys = {resolved: {}, report: []};
            optimize.interface.report.privacyData  = {resolved: {}, report: []};

            this.getResolvedIssues(deviceCrc);
            this.getResolvedSpace(deviceCrc);
        }
    }

    getResolvedIssues(deviceCrc) {
        const device   = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const optimize = this.usefulService.getNested(device, {}, 'processed', 'optimizer', 'optimize');
        const summary  = this.usefulService.getNested(optimize, {}, 'summary');
        const report   = this.usefulService.getNested(optimize, {}, 'interface', 'report');

        const diskCat         = this.devicesValuesService.diskSpaceCategories;
        const registryKeysCat = this.devicesValuesService.registryKeysCategories;

        const orderDiskSpace = [{name: 'windowsUpdate'}, {name: 'windowsTemp'}, {name: 'windowsDumps'}, {name: 'windowsDangerous'},
                                        {name: 'werFiles'}, {name: 'logFiles'}, {name: 'recycleBin'}];

        const orderRegistryKeys = [{name: 'customControls'}, {name: 'deletedApplications'}, {name: 'helpFiles'}, {name: 'missingFonts'}, {name: 'runList'}, {name: 'sharedDlls'},
                                        {name: 'softwareLocations'}, {name: 'startupItems'}];

        const orderPrivacyData = [{name: 'chromeCookies'}, {name: 'firefoxCookies'}, {name: 'internetCookies'}, {name: 'edgeCookies'}, {name: 'operaCookies'},
                                {name: 'chromeHistory'},{name: 'firefoxHistory'}, {name: 'internetHistory'}, {name: 'edgeHistory'}, {name: 'operaHistory'}, {name: 'chromeTemp'},
                                {name: 'operaTemp'}, {name: 'chromeCache'}, {name: 'firefoxCache'}, {name: 'internetCache'}, {name: 'edgeCache'}, {name: 'operaCache'}];

        let diskSpaceIssues    = 0;
        let registryKeysIssues = 0;
        let privacyDataIssues  = 0;

        for (const item in summary.resolved) {
            if (diskCat.has(item)) {
                diskSpaceIssues = diskSpaceIssues + summary.resolved[item];
                if (summary.resolved[item] !== 0) {
                    const category = orderDiskSpace.find( obj => obj.name === item);
                    category['issues'] = summary.resolved[item];
                    category['space'] = this.usefulService.convertSpace(summary.resolvedSize[item]);
                    category['hasValues'] = true;
                }
            } else if (registryKeysCat.has(item)) {
                registryKeysIssues = registryKeysIssues + summary.resolved[item];
                if (summary.resolved[item] !== 0) {
                    const category = orderRegistryKeys.find( obj => obj.name === item);
                    category['issues'] = summary.resolved[item];
                    category['hasValues'] = true;
                }
            } else {
                privacyDataIssues = privacyDataIssues + summary.resolved[item];
                if (summary.resolved[item] !== 0) {
                    const category = orderPrivacyData.find( obj => obj.name === item);
                    category['issues'] = summary.resolved[item];
                    category['space'] = this.usefulService.convertSpace(summary.resolvedSize[item]);
                    category['hasValues'] = true;
                }
            }
        }
        report.diskSpace.report    = orderDiskSpace.filter(item => item.hasOwnProperty('hasValues'));
        report.registryKeys.report = orderRegistryKeys.filter(item => item.hasOwnProperty('hasValues'));
        report.privacyData.report  = orderPrivacyData.filter(item => item.hasOwnProperty('hasValues'));

        report.diskSpace.resolved.issues    = diskSpaceIssues;
        report.registryKeys.resolved.issues = registryKeysIssues;
        report.privacyData.resolved.issues  = privacyDataIssues;
    }

    getResolvedSpace(deviceCrc) {
        const device   = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const optimize = this.usefulService.getNested(device, {}, 'processed', 'optimizer', 'optimize');
        const summary  = this.usefulService.getNested(optimize, {}, 'summary');
        const report  = this.usefulService.getNested(optimize, {}, 'interface', 'report');

        const diskCat         = this.devicesValuesService.diskSpaceCategories;
        const registryKeysCat = this.devicesValuesService.registryKeysCategories;

        let diskSpace         = 0;
        let registryKeysSpace = 0;
        let privacyDataSpace  = 0;

        for (const item in summary.resolvedSize) {
            if (diskCat.has(item)) {
                diskSpace = diskSpace + summary.resolvedSize[item];
            } else if (registryKeysCat.has(item)) {
                registryKeysSpace = registryKeysSpace + summary.resolvedSize[item];
            } else {
                privacyDataSpace = privacyDataSpace + summary.resolvedSize[item];
            }
        }

        report.diskSpace.resolved.space    = this.usefulService.convertSpace(diskSpace);
        report.registryKeys.resolved.space = this.usefulService.convertSpace(registryKeysSpace);
        report.privacyData.resolved.space  = this.usefulService.convertSpace(privacyDataSpace);
    }

    createOneClickOptimizerInterfaceStartup(deviceCrc) {
        const device = this.devicesService.retrieveDeviceByCrc(deviceCrc);
        const startup = this.usefulService.getNested(device, {}, 'processed', 'optimizer', 'startup');

        if (!startup.interface) {
            startup.interface = {};
        }

        let details = {};

        if (startup.summary) {
            if (!startup.summary.boot_total_time) {
                details = this.devicesValuesService.deviceStartupCases.rebootNeeded;
            } else {
                details = this.devicesValuesService.deviceStartupCases.reportAvailable;
            }
        } else {
            details = this.devicesValuesService.deviceStartupCases.noReportAvailable;
        }

        startup.interface.status = details;
        if (startup.last_summary_timestamp) {
            startup.interface.date = this.usefulService.convertDate(startup.last_summary_timestamp);
            startup.interface.dateTime = this.usefulService.timeAgo(startup.last_summary_timestamp);
        }
    }

    createVulnerabilityInterface(deviceId, type) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        let vulnerabilityInterface = {};
        const scan = this.usefulService.getNested(device, {}, "processed", "vulnerability", type);
        const summary = this.usefulService.getNested(device, {}, "processed", "vulnerability", type, "summary");
        const taskState = this.usefulService.getNested(device, true, "processed", "vulnerability", type, "task_state");
        const lastSummary = this.usefulService.getNested(device, 0, "processed", "vulnerability", type, "last_summary_timestamp");
        const pending = taskState === this.devicesValuesService.taskStates.PENDING;
        const running = taskState === this.devicesValuesService.taskStates.RUNNING;
        let newReport = false;

        try {
            const lastScanRead = localStorage.getItem((this.devicesValuesService.vulnerabilityStorage + device.device_id));
            const lastScanReadNumber = Number.parseInt(lastScanRead);
            if (lastScanRead) {
                if (lastSummary > lastScanReadNumber) {
                    newReport = true;
                }
            } else {
                const firstLogin = localStorage.getItem(this.valuesService.loginTimestamp);
                const firstLoginNumber = Number.parseInt(firstLogin);
                if (firstLoginNumber && lastSummary > firstLoginNumber) {
                    newReport = true;
                }
            }
        } catch {
            console.error("Something wrong when checking for new report");
        }

        vulnerabilityInterface["newScan"] = newReport && !this.utilsCommonService.isEmptyObject(summary);
        vulnerabilityInterface["pending"] = pending;
        vulnerabilityInterface["running"] = running;
        vulnerabilityInterface["processing"] = running || pending;
        vulnerabilityInterface["reportDate"] = this.usefulService.timeAgo(lastSummary);
        vulnerabilityInterface["noReportHistory"] = this.utilsCommonService.isEmptyObject(scan) || !scan.summary;
        vulnerabilityInterface["hasReport"] = !this.utilsCommonService.isEmptyObject(summary) || (Array.isArray(summary));

        scan.interface = vulnerabilityInterface;
    }

    computeRiskForVulnerabilityScan(risk) {
        const vulnerabilityRisks = {
            0: this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.low],
            1: this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.low],
            2: this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.medium],
            3: this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.high]
        }

        if (vulnerabilityRisks[risk]) {
            return vulnerabilityRisks[risk];
        }
        return this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.high];
    }

    computeRiskForVulnerabilityAssesment(risk) {
        if (risk < 4) {
            return this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.low];
        }

        if (risk >= 4 && risk < 7) {
            return this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.medium];
        }

        return this.devicesValuesService.riskProperties[this.devicesValuesService.riskIdentifiers.high];
    }

    computeNameForVulnerabilityAssesment(vulnerability) {
        const setDetailsForCveID = () => {
            details = {...this.devicesValuesService.vulnerabilityAssesmentNames.cve.default};
            details.cveLink = details.cveLink.concat(vulnerability.cve_id);
            details.cveId = vulnerability.cve_id;
        };

        const defaultDetails = {
            title     : "vulnerabilityScan.modal.scan.issues.default",
            isDefault : true
        };
        let details: any = {};

        if (vulnerability.cwe_type?.length > 0) {
            details = {...this.devicesValuesService.vulnerabilityAssesmentNames[vulnerability.type]?.[vulnerability.cwe_type[0]]};
            if (Object.keys(details).length !== 0) {
                details.link = this.adobeDataLayerService.addRefAndVisitor(details?.link);
            } else if (vulnerability.cve_id && vulnerability.type === VulnerabilityType.CVE) {
                setDetailsForCveID();
            }
        } else if (vulnerability.cve_id && vulnerability.type === VulnerabilityType.CVE) {
            setDetailsForCveID();
        } else if (vulnerability.type === VulnerabilityType.CVE) {
            details = defaultDetails;
        } else {
            details = {...this.devicesValuesService.vulnerabilityAssesmentNames[vulnerability.type]};
        }

        return Object.keys(details).length !== 0 ? details : defaultDetails;
    }

    createVulnerabilitySummaryInterface(deviceId, type) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        const scan = this.usefulService.getNested(device, {}, "processed", "vulnerability", type);
        const summary = this.usefulService.getNested(device, {}, "processed", "vulnerability", type, "summary");
        const lastSummary = this.usefulService.getNested(device, 0, "processed", "vulnerability", type, "last_summary_timestamp");
        const winUpdates = this.usefulService.getNested(device, {}, "processed", "vulnerability", type, "summary", "winUpdates");
        const vulnerableApps = this.usefulService.getNested(device, [], "processed", "vulnerability", type, "summary", "vulnerableApps");
        const userPass = this.usefulService.getNested(device, [], "processed", "vulnerability", type, "summary", "userPass");
        const summaryInterface = {};
        let content = [];
        let threats = 0;

        if (!scan.summary || this.utilsCommonService.isEmptyObject(summary)) {
            return;
        }

        if (type === this.devicesValuesService.tasksName.vulnerability.scan) {
            summaryInterface["winUpdates"] = this.computeRiskForVulnerabilityScan(winUpdates["risk"]);
            summaryInterface["winUpdates"]["count"] = winUpdates.count && winUpdates.count > 0 ? winUpdates.count : 0;

            const apps = [];
            for (const app of vulnerableApps) {
                if (!app.skipped) {
                    const item = {...this.computeRiskForVulnerabilityScan(app["risk"])};
                    item["risk"] = app["risk"];
                    item["name"] = app["name"];
                    apps.push(item);
                }
            }
            summaryInterface["vulnerableApps"] = { count: apps.length };

            const passwords = [];
            for (const pass of userPass) {
                const item = {...this.computeRiskForVulnerabilityScan(pass["risk"])};
                item["risk"] = pass["risk"];
                item["name"] = pass["name"];
                passwords.push(item);
            }
            summaryInterface["userPass"] = { count: passwords.length };

            content = content.concat(apps).concat(passwords);

            threats = winUpdates.count && winUpdates.count > 0 ? winUpdates.count : 0;
            threats += apps.length;
            threats += passwords.length;
        } else {
            const vulnerabilities = [];
            for (const vulnerability of summary) {
                const item = {...this.computeRiskForVulnerabilityAssesment(vulnerability["cvss_score"])};
                item["risk"] = vulnerability["risk"];
                item["details"] = this.computeNameForVulnerabilityAssesment(vulnerability);
                item['targetName'] = vulnerability.targets.length ? vulnerability.targets[0].name : '';
                vulnerabilities.push(item);
            }
            vulnerabilities.sort(function(a, b) {return b.risk - a.risk});

            content = vulnerabilities;

            threats = winUpdates.count && winUpdates.count > 0 ? winUpdates.count : 0;
            threats += vulnerabilities.length;
        }

        summaryInterface["content"] = content;
        summaryInterface["hasThreats"] = threats > 0;
        summaryInterface["reportDate"] = lastSummary;
        summaryInterface["hasReportDate"] = !this.utilsCommonService.isEmptyObject(summary) && lastSummary;

        device.processed.vulnerability[type].summary.interface = summaryInterface;
    }

    cacheVulnerabilityReport(deviceId, scanType) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        const lastSummary = this.usefulService.getNested(device, 0, "processed", "vulnerability", scanType, "last_summary_timestamp");
        if (lastSummary) {
            localStorage.setItem((this.devicesValuesService.vulnerabilityStorage + device.device_id), lastSummary);
        }
    }

    createMalwareInterface(deviceId) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        for (const type in this.devicesValuesService.tasksName.malware) {
            const scan = this.usefulService.getNested(device, {}, "processed", "malware", this.devicesValuesService.tasksName.malware[type]);
            const lastSummary = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "last_summary_timestamp");
            const summary = this.usefulService.getNested(device, null, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary");
            const taskState = this.usefulService.getNested(device, true, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "task_state");
            const pending = taskState === this.devicesValuesService.taskStates.PENDING;
            const running = taskState === this.devicesValuesService.taskStates.RUNNING;
            const idle = taskState === this.devicesValuesService.taskStates.IDLE;
            let newRaport = false;

            try {
                const lastScanRead = localStorage.getItem((this.devicesValuesService.tasksName.malware[type] + device.device_id));
                if (lastScanRead) {
                    const lastScanReadNumber = Number.parseInt(lastScanRead);
                    if (lastSummary > lastScanReadNumber) {
                        newRaport = true;
                    }
                } else {
                    const firstLogin = localStorage.getItem(this.valuesService.loginTimestamp);
                    const firstLoginNumber = Number.parseInt(firstLogin);
                    if (firstLoginNumber && lastSummary > firstLoginNumber) {
                        newRaport = true;
                    }
                }
            } catch {
                console.error("Something wrong when checking for new report");
            }

            const malwareInterface = {};
            malwareInterface["noHistory"] = this.utilsCommonService.isEmptyObject(scan) || (!scan.summary && idle);
            malwareInterface["noReportHistory"] = this.utilsCommonService.isEmptyObject(scan) || !scan.summary;
            malwareInterface["newScan"] = newRaport && !this.utilsCommonService.isEmptyObject(summary);
            malwareInterface["hasOldReportDate"] = !newRaport && !this.utilsCommonService.isEmptyObject(summary) && lastSummary && idle;
            malwareInterface["reportDate"] = this.usefulService.timeAgo(lastSummary);
            malwareInterface["hasReport"] = !this.utilsCommonService.isEmptyObject(summary);
            malwareInterface["pending"] = pending;
            malwareInterface["running"] = running;
            malwareInterface["processing"] = running || pending;
            malwareInterface["isAndroid"] = device.device_os === OperatingSystems.ANDROID;
            malwareInterface["isIOS"] = device.device_os === OperatingSystems.IOS;

            scan.interface = malwareInterface;
        }
    }

    cacheMalwareReport(deviceId, scanType) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        const lastSummary = this.usefulService.getNested(device, 0, "processed", "malware", scanType, "last_summary_timestamp");
        if (lastSummary) {
            localStorage.setItem((scanType + device.device_id), lastSummary);
        }
    }

    createSummaryInterface(deviceId) {
        const device = this.devicesService.retrieveDeviceById(deviceId);
        for (const type in this.devicesValuesService.tasksName.malware) {
            const scan = this.usefulService.getNested(device, {}, "processed", "malware", this.devicesValuesService.tasksName.malware[type]);
            const summary = this.usefulService.getNested(device, {}, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary");
            const lastSummary = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "last_summary_timestamp");
            let resolved = 0;
            let unresolved = 0;
            let scanned = 0;
            let muted = 0;
            let resolvedDetails = [];
            let mutedDetails = [];
            let unresolvedDetails = [];
            let hasThreats = false;

            if (!scan.summary) {
                continue;
            }

            if (!this.utilsCommonService.isEmptyObject(summary)) {
                if (device.device_os === OperatingSystems.ANDROID) {
                    resolved = 0;
                    unresolved = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary", "apps_unresolved");
                    scanned = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary", "scanned_apps");
                    hasThreats = (unresolved + resolved) > 0;
                } else if (device.device_os === OperatingSystems.IOS) {
                    const issuesIOS = this.computeIOSIssuesInfo(summary);
                    resolvedDetails = issuesIOS.resolvedDetails;
                    unresolvedDetails = issuesIOS.unresolvedDetails;
                    mutedDetails = issuesIOS.mutedDetails;

                    muted = mutedDetails.length;
                    resolved = resolvedDetails.length;
                    unresolved = unresolvedDetails.length;
                    hasThreats = unresolved > 0;
                } else {
                    resolved = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary", "files_resolved");
                    unresolved = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary", "files_unresolved");
                    scanned = this.usefulService.getNested(device, 0, "processed", "malware", this.devicesValuesService.tasksName.malware[type], "summary", "scanned_items");
                    hasThreats = (unresolved + resolved) > 0;
                }
            }

            const summaryInterface = {};
            summaryInterface["resolvedDetails"] = resolvedDetails;
            summaryInterface["unresolvedDetails"] = unresolvedDetails;
            summaryInterface["mutedDetails"] = mutedDetails;

            summaryInterface["hasThreats"] = hasThreats;
            summaryInterface["isAndroid"] = device.device_os !== OperatingSystems.ANDROID;
            summaryInterface["resolved"] = resolved;
            summaryInterface["unresolved"] = unresolved;
            summaryInterface["muted"] = muted;
            summaryInterface["scanned"] = scanned;
            summaryInterface["total"] = resolved + unresolved;
            summaryInterface["hasReportDate"] = !this.utilsCommonService.isEmptyObject(summary) && lastSummary;
            summaryInterface["reportDate"] = lastSummary;
            summaryInterface["isIOS"] = device.device_os === OperatingSystems.IOS;

            device.processed.malware[this.devicesValuesService.tasksName.malware[type]].summary.interface = summaryInterface;
        }

    }

    computeIOSIssuesInfo(summary) {
        const resolvedDetails = [];
        const unresolvedDetails = [];
        const mutedDetails = [];
        const osOutdated = !!summary.os_version; // 1 - outdated, 0 - updated
        const passcodeNotSet = !!summary.passcode; // 1 - not set; 0 - set
        const wifiDisconnected = !(summary?.wifi?.connected || summary?.connected?.connected);
        const wifiUnsafe = summary?.wifi?.unsafe_wifi || summary?.connected?.unsafe_wifi;
        const accountLeaks = !!summary.new_account_leaks; // 1 - leaks; 0 - no leaks

        const issues = [
            {
                title: "malwareScan.ios.modal.scan.security.report.ios",
                isUnresolved: osOutdated,
                status: osOutdated
                        ? 'malwareScan.ios.modal.scan.security.report.ios.outdated'
                        : 'malwareScan.ios.modal.scan.security.report.ios.latest',
                icon: 'connect_icon_ios',
                description: osOutdated
                            ? 'malwareScan.ios.modal.scan.security.report.ios.outdated.description'
                            : 'malwareScan.ios.modal.scan.security.report.ios.latest.description'
            },
            {
                title: "malwareScan.ios.modal.scan.security.report.deviceAccess",
                isUnresolved: passcodeNotSet,
                status: passcodeNotSet
                        ? 'malwareScan.ios.modal.scan.security.report.deviceAccess.unprotected'
                        : 'malwareScan.ios.modal.scan.security.report.deviceAccess.secured',
                icon: 'connect_icon_lock_open',
                description: passcodeNotSet
                            ? 'malwareScan.ios.modal.scan.security.report.deviceAccess.unprotected.description'
                            : 'malwareScan.ios.modal.scan.security.report.deviceAccess.secured.description'
            },
            {
                title: "malwareScan.ios.modal.scan.security.report.wifiSecurity",
                isUnresolved: !wifiDisconnected && wifiUnsafe,
                status: wifiDisconnected
                        ? 'malwareScan.ios.modal.scan.security.report.wifiSecurity.notConnected'
                        : (wifiUnsafe
                            ? 'malwareScan.ios.modal.scan.security.report.wifiSecurity.openWifi'
                            : 'malwareScan.ios.modal.scan.security.report.wifiSecurity.passwordProtected'),
                isMuted: wifiDisconnected,
                icon: 'connect_icon_connected',
                description: wifiDisconnected
                                ? 'malwareScan.ios.modal.scan.security.report.wifiSecurity.notConnected.description'
                                : (wifiUnsafe
                                    ? 'malwareScan.ios.modal.scan.security.report.wifiSecurity.openWifi.description'
                                    : 'malwareScan.ios.modal.scan.security.report.wifiSecurity.passwordProtected.description')
            },
            {
                title: "malwareScan.ios.modal.scan.security.report.accountPrivacy",
                isUnresolved: accountLeaks,
                status: accountLeaks
                        ? 'malwareScan.ios.modal.scan.security.report.accountPrivacy.breachFound'
                        : 'malwareScan.ios.modal.scan.security.report.accountPrivacy.noBreaches',
                icon: 'connect_icon_private_data',
                description: accountLeaks
                            ? 'malwareScan.ios.modal.scan.security.report.accountPrivacy.breachFound.description'
                            : 'malwareScan.ios.modal.scan.security.report.accountPrivacy.noBreaches.description'
            },
        ];

        for (const issue of issues) {
            if (issue.isUnresolved) {
                unresolvedDetails.push(issue);
            } else {
                if (issue.isMuted) {
                    mutedDetails.push(issue);
                } else {
                    resolvedDetails.push(issue);
                }
            }
        }

        return { resolvedDetails, unresolvedDetails, mutedDetails };
    }

}
